import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  BackButton, DataTable, SearchFilterBar,
  type ColumnDef, type FilterDef, type RowMenuItem, type SortState,
} from '@shared-ui/components';
import { LatexText } from '@shared-ui/latex';
import { adminApi } from '../../../api/adminApi';
import type { AssignmentKey, AssignmentQuestionRow } from '../../../types/assignmentQuality';
import { configLabel } from '../../../types/assignmentQuality';
import { assignmentPath, studentAnalysesPath, analysisDetailPath, questionDetailPath, studentDetailPath } from './assignmentRoutes';
import { PopOut } from './AssignmentQualityDetail';
import { AnswerCell, AssessmentQualityCell, CorrectnessBadge, HealthCell, ReviewCell, ScoreCell, StepVerdictsCell } from './qualityCells';
import { StepVerdictDrawer } from './StepVerdictDrawer';
import { authApi } from '../../../api/auth';
import { sourceColumn, sourceGroupRow } from './columnSources';
import './AssignmentQuality.css';

const PAGE_SIZE = 25;

/** Score bands, because filtering on an exact /10 is not a question anyone asks. */
const SCORE_BANDS = [
  { value: 'none', label: 'No score', test: (v?: number) => v === undefined },
  { value: '0', label: '0 — wrong', test: (v?: number) => v === 0 },
  { value: 'low', label: '1–4 — poor', test: (v?: number) => v !== undefined && v >= 1 && v <= 4 },
  { value: 'mid', label: '5–7 — partial', test: (v?: number) => v !== undefined && v >= 5 && v <= 7 },
  { value: 'high', label: '8–10 — good', test: (v?: number) => v !== undefined && v >= 8 },
];

/** Sortable columns → the value each sorts on. Real values, never rendered text:
 *  an undefined score must sort as absent, not as the string "—". */
const SORT_VALUES: Record<string, (r: AssignmentQuestionRow) => string | number> = {
  question: r => r.questionOrder,
  student: r => r.studentName.toLowerCase(),
  attempt: r => r.attemptNumber ?? 0,
  config: r => `${r.config ?? ''}|${r.model ?? ''}`,
  correctAnswer: r => (r.correctAnswer ?? '').toLowerCase(),
  llmRead: r => (r.llmStudentAnswer ?? '').toLowerCase(),
  tutorVerdict: r => r.correctness ?? '',
  studentAccuracy: r => r.correctnessScore ?? -1,
  studentApproach: r => r.approachScore ?? -1,
  assessmentQuality: r => r.assessmentQuality?.verdict ?? '',
  health: r => r.qualityChecks.length + (r.status === 'failed' ? 100 : 0) + (r.thumbsDown ? 10 : 0),
};

/**
 * Every analysis one assignment produced — all questions, all students, all
 * attempts.
 *
 * Scope is deliberately the assignment rather than one question: the useful
 * cuts run both ways ("this question across the class", "this student across
 * the paper"), and a filter does both from one payload. Arriving from a matrix
 * cell pre-selects that question; the filter can be cleared.
 */
export default function AssignmentAnalyses() {
  const navigate = useNavigate();
  const adminId = authApi.getUser()?.id;
  const { questionSetId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();

  const key: AssignmentKey = useMemo(() => ({
    questionSetId,
    assignedById: params.get('by') ?? '',
    batchId: params.get('batchId') ?? undefined,
  }), [questionSetId, params]);

  // Filters live in the URL, so a reload, a bookmark and Back all land on the
  // same view. Seeded from it too — `studentId` arrives that way from the
  // student table.
  const [filters, setFiltersState] = useState<Record<string, string>>(() => ({
    dupes: params.get('dupes') ?? '',
    superseded: params.get('superseded') ?? '',
    question: params.get('questionId') ?? '',
    student: params.get('studentId') ?? '',
    verdict: params.get('verdict') ?? '',
    accuracy: params.get('accuracy') ?? '',
    quality: params.get('quality') ?? '',
    search: params.get('q') ?? '',
  }));
  const [sort, setSort] = useState<SortState>({ sortBy: 'question', sortOrder: 'asc' });
  const [page, setPage] = useState(1);

  /** Filter keys → their URL parameter. The assignment key (`by`, `batchId`)
   *  is NOT here: it identifies the page, and a filter must never drop it. */
  const URL_KEYS: Record<string, string> = {
    question: 'questionId', student: 'studentId', verdict: 'verdict',
    accuracy: 'accuracy', quality: 'quality', search: 'q', superseded: 'superseded',
    dupes: 'dupes',
  };

  const setFilters = (next: Record<string, string>) => {
    setFiltersState(next);
    setPage(1);
    const url = new URLSearchParams(params);
    for (const [key, param] of Object.entries(URL_KEYS)) {
      if (next[key]) url.set(param, next[key]);
      else url.delete(param);
    }
    // replace, not push: filtering is refining one view, and Back should leave
    // the page rather than walk every keystroke of it.
    setParams(url, { replace: true });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['assignmentAnalyses', key.questionSetId, key.assignedById, key.batchId],
    queryFn: () => adminApi.getAssignmentAnalyses(key),
    enabled: !!key.questionSetId && !!key.assignedById,
  });

  const review = useMutation({
    mutationFn: (v: { submissionId: string; rating: 'positive' | 'negative'; comment?: string }) =>
      adminApi.submitAdminFeedback(v.submissionId, v.rating, v.comment),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assignmentAnalyses'] }),
  });

  const judge = useMutation({
    mutationFn: () => adminApi.judgeAssignment(key),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assignmentAnalyses'] }),
  });

  // Which row's steps are open. Captured lazily: a row already carrying
  // `benchRow` skips the write entirely and opens straight away.
  const [stepRow, setStepRow] = useState<{ datasetId: string; scenarioInputId: string } | null>(null);
  const openSteps = useMutation({
    mutationFn: (row: AssignmentQuestionRow) =>
      row.benchRow
        ? Promise.resolve(row.benchRow)
        : adminApi.ensureAssignmentBenchRow(key, row.submissionId!),
    onSuccess: r => setStepRow({ datasetId: r.datasetId, scenarioInputId: r.scenarioInputId }),
  });

  const rows = useMemo(() => data?.rows ?? [], [data]);

  const filterDefs: FilterDef[] = useMemo(() => [
    {
      key: 'question',
      placeholder: 'Any question',
      options: (data?.questions ?? []).map(q => ({
        value: q.id,
        label: `${q.order + 1}. ${(q.questionText ?? q.id).replace(/\$[^$]*\$/g, '…').slice(0, 60)}`,
      })),
    },
    {
      key: 'student',
      placeholder: 'Any student',
      options: [...new Map(rows.map(r => [r.studentId, r.studentName])).entries()]
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([value, label]) => ({ value, label })),
    },
    {
      key: 'verdict',
      placeholder: 'Any app verdict',
      options: [...new Set(rows.map(r => r.correctness).filter((v): v is string => !!v))]
        .sort().map(v => ({ value: v, label: v.replace(/_/g, ' ') })),
    },
    { key: 'accuracy', placeholder: 'Any accuracy score', options: SCORE_BANDS.map(b => ({ value: b.value, label: b.label })) },
    {
      key: 'dupes',
      placeholder: 'All analyses',
      options: [
        { value: 'unique', label: 'Latest per student + question' },
        { value: 'only', label: 'Duplicates only' },
      ],
    },
    {
      key: 'superseded',
      placeholder: 'Current and superseded',
      options: [
        { value: 'current', label: 'Current only' },
        { value: 'superseded', label: 'Superseded only' },
      ],
    },
    {
      key: 'quality',
      placeholder: 'Any assessment quality',
      options: [
        { value: 'unjudged', label: 'Not judged' },
        { value: 'good', label: 'Judged good' },
        { value: 'acceptable', label: 'Judged acceptable' },
        { value: 'bad', label: 'Judged bad' },
      ],
    },
  ], [data, rows]);

  const filtered = useMemo(() => {
    const needle = (filters.search ?? '').trim().toLowerCase();
    const band = SCORE_BANDS.find(b => b.value === filters.accuracy);
    return rows.filter(r => {
      if (filters.superseded === 'current' && r.current === false) return false;
      if (filters.superseded === 'superseded' && r.current !== false) return false;
      if (filters.question && r.questionId !== filters.question) return false;
      if (filters.student && r.studentId !== filters.student) return false;
      if (filters.verdict && r.correctness !== filters.verdict) return false;
      if (band && !band.test(r.correctnessScore)) return false;
      if (filters.quality === 'unjudged' && r.assessmentQuality) return false;
      if (filters.quality && filters.quality !== 'unjudged'
        && r.assessmentQuality?.verdict !== filters.quality) return false;
      if (!needle) return true;
      return r.studentName.toLowerCase().includes(needle)
        || (r.questionText ?? '').toLowerCase().includes(needle)
        || (r.correctAnswer ?? '').toLowerCase().includes(needle)
        || (r.llmStudentAnswer ?? '').toLowerCase().includes(needle)
        || (r.answer.text ?? '').toLowerCase().includes(needle);
    });
  }, [rows, filters]);

  /**
   * A (student, question) pair can hold several analyses — one per attempt,
   * plus any superseded inside an attempt. `unique` keeps the newest of each;
   * `only` keeps just the pairs that HAVE more than one, which is how you find
   * the re-runs and retakes worth comparing.
   *
   * Newest is chosen by submission time, independent of the table's sort — the
   * survivor of a dedupe must not change because someone sorted by score.
   */
  const deduped = useMemo(() => {
    const mode = filters.dupes;
    if (!mode) return filtered;

    const groups = new Map<string, typeof filtered>();
    for (const row of filtered) {
      const key = `${row.studentId}::${row.questionId}`;
      groups.set(key, [...(groups.get(key) ?? []), row]);
    }

    const out: typeof filtered = [];
    for (const group of groups.values()) {
      if (mode === 'only') {
        if (group.length > 1) out.push(...group);
        continue;
      }
      // Superseded analyses are never the survivor: the student was shown the
      // one that replaced them, so keeping one here would report an answer they
      // never saw. Dropping them first makes the row count equal the number of
      // (student, question) attempts actually made.
      const live = group.filter(r => r.current !== false);
      const candidates = live.length > 0 ? live : group;
      out.push([...candidates].sort((a, b) =>
        (b.submittedAt ?? '').localeCompare(a.submittedAt ?? ''))[0]);
    }
    return out;
  }, [filtered, filters.dupes]);

  const sorted = useMemo(() => {
    const valueOf = SORT_VALUES[sort.sortBy];
    if (!valueOf) return deduped;
    const factor = sort.sortOrder === 'asc' ? 1 : -1;
    // Copy: react-query's cached array must not be reordered in place.
    return [...deduped].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av === bv) return 0;
      return (typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv))) * factor;
    });
  }, [deduped, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  // A filter change can shrink the list under the current page; clamp rather
  // than strand the user on a page they never navigated to.
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [sorted, safePage],
  );

  /** Right-click a row to narrow to what it is an example OF. The two cuts the
   *  page exists for — this question across the class, this student across the
   *  paper — are one gesture from any row rather than two dropdown hunts. */
  const rowMenu = (row: AssignmentQuestionRow): RowMenuItem[] => [
    {
      key: 'student',
      label: `Filter to ${row.studentName}`,
      disabled: filters.student === row.studentId,
      onSelect: () => setFilters({ ...filters, student: row.studentId }),
    },
    {
      key: 'question',
      label: `Filter to question ${row.questionOrder + 1}`,
      disabled: filters.question === row.questionId,
      onSelect: () => setFilters({ ...filters, question: row.questionId }),
    },
    {
      key: 'both',
      label: 'Filter to this student AND question',
      disabled: filters.student === row.studentId && filters.question === row.questionId,
      onSelect: () => setFilters({ ...filters, student: row.studentId, question: row.questionId }),
    },
    {
      key: 'clear',
      label: 'Clear student and question filters',
      separatorBefore: true,
      disabled: !filters.student && !filters.question,
      onSelect: () => setFilters({ ...filters, student: '', question: '' }),
    },
  ];

  const columns: ColumnDef<AssignmentQuestionRow>[] = useMemo(() => [
    sourceColumn<AssignmentQuestionRow>('question', row => (
      <div className="aq-qcell">
        {row.questionOrder + 1}. <LatexText text={row.questionText || row.questionId} truncate={120} />
        <PopOut to={questionDetailPath(row.questionId)} label="Open this question in a new tab" />
        <div className="aq-muted">{row.questionType}</div>
      </div>
    ), { sortable: true, mobile: 'title' }),
    sourceColumn<AssignmentQuestionRow>('correctAnswer', row => (
      row.correctAnswer ? <LatexText text={row.correctAnswer} /> : <span className="aq-muted">—</span>
    ), { sortable: true }),
    sourceColumn<AssignmentQuestionRow>('student', row => (
      <div className="aq-student" onClick={e => e.stopPropagation()}>
        <Link className="aq-link" to={studentAnalysesPath(row.studentId, key.questionSetId)}>
          {row.studentName}
        </Link>
        <PopOut to={studentDetailPath(row.studentId)} label="Open this student in a new tab" />
        {row.grantStatus !== 'active' && <span className="aq-badge warn">{row.grantStatus}</span>}
      </div>
    ), { sortable: true, width: '9rem' }),
    sourceColumn<AssignmentQuestionRow>('attempt', row => (
      <div className="aq-attempt">
        {row.attemptNumber === undefined
          ? <span className="aq-muted">—</span>
          : <span className={(row.attemptCount ?? 1) > 1 ? '' : 'aq-muted'}>
              {row.attemptNumber}{(row.attemptCount ?? 1) > 1 ? `/${row.attemptCount}` : ''}
            </span>}
        {row.submittedAt && (
          <span className="aq-muted">{format(new Date(row.submittedAt), 'd MMM HH:mm')}</span>
        )}
        {/* The attempt stopped pointing at this analysis — a re-submission or a
            re-evaluation replaced it. It still ran, so it still counts. */}
        {row.current === false && (
          <span className="aq-badge warn" title={row.rerunOf ? 'Replaced by a re-run of this analysis' : 'Replaced by a later submission of this question'}>
            superseded
          </span>
        )}
      </div>
    ), { sortable: true, width: '6.5rem' }),
    sourceColumn<AssignmentQuestionRow>('config', row => (
      <div className="aq-attempt">
        <span>{row.config ? configLabel(row.config) : '—'}</span>
        {/* The config names a pipeline; the model is what actually ran under
            it, and the two drift apart as a config is edited. */}
        {row.model && <span className="aq-muted" title={row.provider}>{row.model}</span>}
      </div>
    ), { sortable: true }),
    sourceColumn<AssignmentQuestionRow>('studentAnswer', row => (
      <AnswerCell answer={row.answer} fallback={row.skipped ? 'skipped' : '—'} />
    )),
    sourceColumn<AssignmentQuestionRow>('llmRead', row => (
      row.llmStudentAnswer ? <LatexText text={row.llmStudentAnswer} /> : <span className="aq-muted">—</span>
    ), { sortable: true }),
    sourceColumn<AssignmentQuestionRow>('tutorVerdict', row => (
      <CorrectnessBadge value={row.correctness} />
    ), { sortable: true }),
    sourceColumn<AssignmentQuestionRow>('studentAccuracy', row => (
      <ScoreCell value={row.correctnessScore} />
    ), { sortable: true, extraClass: 'aq-num' }),
    sourceColumn<AssignmentQuestionRow>('studentApproach', row => (
      <ScoreCell value={row.approachScore} />
    ), { sortable: true, extraClass: 'aq-num' }),
    sourceColumn<AssignmentQuestionRow>('assessmentQuality', row => (
      <AssessmentQualityCell
        facts={row}
        onJudge={judge.isPending ? undefined : () => judge.mutate()}
        judging={judge.isPending}
      />
    ), { sortable: true }),
    sourceColumn<AssignmentQuestionRow>('stepVerdicts', row => (
      row.submissionId
        ? <StepVerdictsCell
            facts={row}
            pending={openSteps.isPending && openSteps.variables?.submissionId === row.submissionId}
            onOpen={() => openSteps.mutate(row)}
          />
        : <span className="aq-muted">—</span>
    )),
    sourceColumn<AssignmentQuestionRow>('review', row => (
      row.submissionId
        ? <ReviewCell
            facts={row}
            adminId={adminId}
            pending={review.isPending && review.variables?.submissionId === row.submissionId}
            onSubmit={(rating, comment) => review.mutate({ submissionId: row.submissionId!, rating, comment })}
          />
        : <span className="aq-muted">—</span>
    )),
    // Last, and outside the judge band: health is deterministic checks, not
    // anyone's judgment. It sat mid-band before, which put it under "Judged
    // quality" and pushed My review under "Automatic checks".
    sourceColumn<AssignmentQuestionRow>('health', row => <HealthCell facts={row} />, { sortable: true }),
  ], [key.questionSetId, judge, review, openSteps, adminId]);

  if (!key.assignedById) return <div className="error">Missing assigner — open this page from the Assignments list.</div>;
  if (error) return <div className="error">Error: {(error as Error).message}</div>;

  const assignment = data?.assignment;
  const studentCount = new Set(rows.map(r => r.studentId)).size;

  return (
    <div className="aq-page">
      <BackButton label="assignment" to={assignmentPath(key)} />

      <div className="aq-head">
        <h2>{assignment?.title ?? 'Assignment'}</h2>
        <p className="aq-sub">
          {[
            assignment?.subject,
            assignment?.modality,
            assignment && `assigned by ${assignment.assignedByName || assignment.assignedById.slice(0, 12)}`,
            assignment && `${assignment.totalQuestions} questions`,
            `${assignment?.students ?? studentCount} students`,
            assignment && `created ${format(new Date(assignment.createdAt), 'd MMM yyyy')}`,
            assignment?.dueAt && `due ${assignment.dueAt}`,
            `${rows.length} analyses`,
          ].filter(Boolean).join(' · ')}
        </p>
      </div>



      {judge.error && <p className="aq-warn">Judging failed: {(judge.error as Error).message}</p>}
      {openSteps.error && (
        <p className="aq-warn">Could not open steps: {(openSteps.error as Error).message}</p>
      )}
      {judge.data && (
        <p className="aq-note">
          Judged {judge.data.judge.cellsJudged} of {judge.data.judge.cellsConsidered} cells
          {judge.data.judge.failedBatches > 0 ? ` — ${judge.data.judge.failedBatches} batches failed` : ''}.
        </p>
      )}

      <DataTable
        toolbar={
          <SearchFilterBar
          filters={filterDefs}
          values={filters}
          onChange={setFilters}
          searchPlaceholder="question, student, answer"
          // Everything filters in memory over one fetched assignment, so there is
          // nothing for a Fetch button to commit.
          hideFetch
          />
        }
        columns={columns}
        columnGroups={sourceGroupRow([
          ['question', 2], ['attempt', 4], ['tutor', 2], ['scorer', 2], ['judge', 3], ['checks', 1],
        ])}
        data={pageRows}
        rowKey={row => `${row.studentId}:${row.sessionId ?? ''}:${row.questionId}`}
        // The row IS the analysis, so opening it is the row's own action —
        // a column of "open →" links was a second control for the same thing.
        onRowClick={row => row.submissionId && navigate(analysisDetailPath(row.submissionId))}
        rowHref={row => (row.submissionId ? analysisDetailPath(row.submissionId) : undefined)}
        rowClickable={row => !!row.submissionId}
        rowMenu={rowMenu}
        loading={isLoading}
        emptyMessage="No analyses match these filters."
        sort={sort}
        onSortChange={next => { setSort(next); setPage(1); }}
        pagination={{
          page: safePage,
          totalPages,
          total: sorted.length,
          onPageChange: setPage,
        }}
      />

      {stepRow && (
        <StepVerdictDrawer
          datasetId={stepRow.datasetId}
          scenarioInputId={stepRow.scenarioInputId}
          onClose={() => setStepRow(null)}
        />
      )}
    </div>
  );
}
